import React from 'react';
import { useChatStore } from '../../store/chatStore';
import { TokenUsage, TokenUsageDisplay } from '../ChatArea/TokenUsageDisplay';

/**
 * Calculates the total token usage across all messages in the current conversation
 */
const useTotalTokenUsage = (): TokenUsage | null => {
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const messages = useChatStore((state) =>
    activeConversationId
      ? state.conversations[activeConversationId]?.messages
      : [],
  );

  if (!messages || messages.length === 0) {
    return null;
  }

  // Filter messages that have token usage data
  const messagesWithTokenUsage = messages.filter(
    (message) => message.tokenUsage,
  );

  if (messagesWithTokenUsage.length === 0) {
    return null;
  }

  // Calculate totals
  const totalUsage = messagesWithTokenUsage.reduce(
    (acc, message) => {
      const usage = message.tokenUsage!;
      return {
        promptTokens: acc.promptTokens + usage.promptTokens,
        completionTokens: acc.completionTokens + usage.completionTokens,
        cacheReadInputTokens:
          acc.cacheReadInputTokens + usage.cacheReadInputTokens,
        cacheWriteInputTokens:
          acc.cacheWriteInputTokens + usage.cacheWriteInputTokens,
        totalTokens: acc.totalTokens + usage.totalTokens,
        promptCost: acc.promptCost + usage.promptCost,
        completionCost: acc.completionCost + usage.completionCost,
        cacheReadInputCost: acc.cacheReadInputCost + usage.cacheReadInputCost,
        cacheWriteInputCost:
          acc.cacheWriteInputCost + usage.cacheWriteInputCost,
        totalCost: acc.totalCost + usage.totalCost,
        savings: acc.savings + usage.savings,
      };
    },
    {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      cacheReadInputTokens: 0,
      cacheWriteInputTokens: 0,
      promptCost: 0,
      completionCost: 0,
      cacheReadInputCost: 0,
      cacheWriteInputCost: 0,
      totalCost: 0,
      savings: 0,
    } as TokenUsage,
  );

  return totalUsage;
};

interface TotalUsageDisplayProps {
  className?: string;
}

export const TotalUsageDisplay: React.FC<TotalUsageDisplayProps> = ({
  className = '',
}) => {
  const totalUsage = useTotalTokenUsage();

  if (!totalUsage) {
    return null;
  }

  return (
    <div className="flex justify-end px-4 pt-2 pb-2">
      <div className="mx-auto flex w-full max-w-3xl justify-start">
        <div className={className}>
          <TokenUsageDisplay tokenUsage={totalUsage} className="text-xs" />
        </div>
      </div>
    </div>
  );
};
